import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Radar, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import { useMarketStore } from '../../store/useMarketStore';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8000';

interface DiscoverParams {
  market_id: string;
  zip_codes: string[];
  min_units: number;
  max_units: number;
  min_price: number | null;
  max_price: number | null;
  limit: number;
}

interface DiscoverResult {
  discovered: number;
  enriched: number;
  saved: number;
  skipped_existing: number;
  errors: number;
  new_property_ids: number[];
}

async function discoverAndIngest(params: DiscoverParams): Promise<DiscoverResult> {
  const response = await fetch(`${API_BASE}/api/discover-and-ingest`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ detail: 'Unknown error' }));
    throw new Error(err.detail || `HTTP ${response.status}`);
  }
  return response.json();
}

export const DiscoverPanel: React.FC = () => {
  const [expanded, setExpanded] = useState(false);
  const [zipInput, setZipInput] = useState('');
  const [minUnits, setMinUnits] = useState(2);
  const [maxUnits, setMaxUnits] = useState(20);
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [limit, setLimit] = useState(25);
  const [lastResult, setLastResult] = useState<DiscoverResult | null>(null);

  const activeMarket = useMarketStore((s) => s.activeMarket);
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: discoverAndIngest,
    onSuccess: (data) => {
      setLastResult(data);
      // Invalidate batch source queries so the table refreshes
      queryClient.invalidateQueries({ queryKey: ['batchSource'] });
    },
  });

  const handleDiscover = () => {
    const zips = zipInput
      .split(',')
      .map((z) => z.trim())
      .filter((z) => /^\d{5}$/.test(z));

    if (zips.length === 0) return;

    mutation.mutate({
      market_id: activeMarket.market_id,
      zip_codes: zips,
      min_units: minUnits,
      max_units: maxUnits,
      min_price: minPrice ? Number(minPrice) : null,
      max_price: maxPrice ? Number(maxPrice) : null,
      limit,
    });
  };

  return (
    <div className="border-b border-slate-700 pb-4">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center justify-between w-full px-1 py-1 group"
      >
        <div className="flex items-center gap-2">
          <Radar className="w-5 h-5 text-emerald-400" />
          <span className="text-sm font-semibold text-ivory">Discover New Deals</span>
        </div>
        {expanded ? (
          <ChevronUp className="w-4 h-4 text-stone-500 group-hover:text-ivory" />
        ) : (
          <ChevronDown className="w-4 h-4 text-stone-500 group-hover:text-ivory" />
        )}
      </button>

      {expanded && (
        <div className="mt-3 space-y-3">
          {/* ZIP Codes */}
          <div>
            <label className="block text-xs font-medium text-stone-400 uppercase tracking-wider mb-1">
              ZIP Codes *
            </label>
            <input
              type="text"
              placeholder={activeMarket.market_id === 'norfolk' ? 'e.g. 23507, 23517' : 'e.g. 19143, 19139'}
              value={zipInput}
              onChange={(e) => setZipInput(e.target.value)}
              className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-sm text-ivory placeholder-stone-500 focus:border-emerald-400 focus:outline-none"
            />
          </div>

          {/* Unit range */}
          <div>
            <label className="block text-xs font-medium text-stone-400 uppercase tracking-wider mb-1">
              Units
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={1}
                value={minUnits}
                onChange={(e) => setMinUnits(Number(e.target.value) || 2)}
                className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-1.5 text-sm text-ivory focus:border-emerald-400 focus:outline-none"
              />
              <span className="text-stone-500 text-xs">to</span>
              <input
                type="number"
                min={1}
                value={maxUnits}
                onChange={(e) => setMaxUnits(Number(e.target.value) || 20)}
                className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-1.5 text-sm text-ivory focus:border-emerald-400 focus:outline-none"
              />
            </div>
          </div>

          {/* Price range */}
          <div>
            <label className="block text-xs font-medium text-stone-400 uppercase tracking-wider mb-1">
              Price Range
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                placeholder="Min $"
                value={minPrice}
                onChange={(e) => setMinPrice(e.target.value)}
                className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-1.5 text-sm text-ivory placeholder-stone-500 focus:border-emerald-400 focus:outline-none"
              />
              <span className="text-stone-500 text-xs">to</span>
              <input
                type="number"
                placeholder="Max $"
                value={maxPrice}
                onChange={(e) => setMaxPrice(e.target.value)}
                className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-1.5 text-sm text-ivory placeholder-stone-500 focus:border-emerald-400 focus:outline-none"
              />
            </div>
          </div>

          {/* Limit */}
          <div>
            <label className="block text-xs font-medium text-stone-400 uppercase tracking-wider mb-1">
              Max Results
            </label>
            <select
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
              className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-1.5 text-sm text-ivory focus:border-emerald-400 focus:outline-none"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>

          {/* Discover button */}
          <button
            onClick={handleDiscover}
            disabled={mutation.isPending || !zipInput.trim()}
            className="w-full flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-700 disabled:text-stone-500 text-white font-medium py-2.5 rounded-lg transition-colors text-sm"
          >
            {mutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Discovering...
              </>
            ) : (
              <>
                <Radar className="w-4 h-4" />
                Pull New Deals
              </>
            )}
          </button>

          {/* Result feedback */}
          {mutation.isError && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-2">
              <p className="text-xs text-red-400">{mutation.error.message}</p>
            </div>
          )}

          {lastResult && !mutation.isPending && (
            <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-2.5 space-y-1">
              <p className="text-xs text-stone-400">
                Found <span className="text-ivory font-medium">{lastResult.discovered}</span> properties
              </p>
              <p className="text-xs text-emerald-400 font-medium">
                +{lastResult.saved} new deals added
              </p>
              {lastResult.skipped_existing > 0 && (
                <p className="text-xs text-stone-500">
                  {lastResult.skipped_existing} already in database
                </p>
              )}
              {lastResult.errors > 0 && (
                <p className="text-xs text-amber-400">
                  {lastResult.errors} could not be processed
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
